import { useDispatch, useSelector } from "react-redux";
import {
  InlineFieldError,
  InputError,
  LevelReq,
} from "../../components/form/FormBody.jsx";
import { Field, FieldArray, Form, Formik, setIn } from "formik";
import {
  buildItemGroupCompleteTouched,
  firstFormikErrorMessage,
  itemGroupDraftSchema,
  itemGroupSchema,
} from "../../utils/validation.util.js";
import instance from "../../utils/api_instance.util.js";
import { resolveUploadUrl } from "../../utils/upload.util.js";
import { errorToastify, successToastify } from "../../utils/toastify.util.js";
import { useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import Button from "../../components/button/Button.jsx";
import IapImageCropperModal from "../../components/image/IapImageCropperModal.jsx";
import { createItemGroup } from "../../features/itemSlice.js";
import { Link, useNavigate } from "react-router-dom";
import { getAllCategories } from "../../features/categorySlice.js";
import { fetchSubCategoryDataByCatagoryId } from "../../features/subCategorySlice.js";
import { getUserDetails } from "../../features/userSlice.js";

/** ✅ Multi-select dropdown (same styles you used in update page) */
const MultiSelectDropdown = ({
  label,
  placeholder = "Search...",
  options = [],
  selectedIds = [],
  onToggle,
  onDone,
  disabled = false,
  required = false,
  invalid = false,
  errorName,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const selectedText = useMemo(() => {
    const count = selectedIds.length || 0;
    return count ? `${count} Selected` : `Select ${label}`;
  }, [selectedIds, label]);

  const filtered = useMemo(() => {
    const q = (search || "").trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) =>
      String(o?.name || o?.title || "")
        .toLowerCase()
        .includes(q),
    );
  }, [options, search]);

  // close on outside click
  const wrapRef = useRef(null);
  useEffect(() => {
    const handler = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="col-md-6 mb-3" ref={wrapRef}>
      <label className="form-label fs-7">
        {label}
        {required ? <span className="text-danger">*</span> : null}
      </label>

      <div className="category-dd">
        <div
          className={`form-control category-dd-input ${disabled ? "bg-light" : ""} ${invalid ? "border-danger" : ""}`}
          role="button"
          onClick={() => {
            if (disabled) return;
            setOpen((v) => !v);
          }}
          style={{
            pointerEvents: disabled ? "none" : "auto",
            opacity: disabled ? 0.7 : 1,
          }}
        >
          <span>{selectedText}</span>
          <span className="category-dd-arrow">{open ? "▲" : "▼"}</span>
        </div>

        {open && (
          <div className="category-dd-panel">
            <input
              type="text"
              className="form-control mb-2"
              placeholder={placeholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            <div className="category-dd-list">
              {filtered.length === 0 ? (
                <div className="text-muted small">No data found</div>
              ) : (
                <div className="category-dd-grid">
                  {filtered.map((item) => (
                    <label key={item.id} className="category-dd-item">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={selectedIds.includes(Number(item.id))}
                        onChange={() => onToggle(Number(item.id))}
                      />
                      <span>{item.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="category-dd-footer">
              <button
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={() => {
                  setOpen(false);
                  onDone && onDone();
                }}
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
      {errorName ? <InputError name={errorName} /> : null}
    </div>
  );
};

const itemGroupData = {
  id: "",
  name: "",
  description: "",
  category: "", // old single select (kept)
  subCategory: "", // old single select (kept)
  price: "0",
  logo: "",
  bannerImg: "",
  iapimage: "",
  status: "draft",
  isDraft: true,
  // ✅ NEW payload fields
  categoryIds: [], // [1,2]
  subCategoryIds: [], // [5,7,9]

  items: [
    {
      item_name: "",
      item_desc: "",
      item_img: "",
      position: 1,
    },
  ],
};

const ItemGroupAddPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isBtnLoad, isRedirect } = useSelector((state) => state.item);
  const { allCategory } = useSelector((state) => state.category);
  const { allSubCatagories } = useSelector((state) => state.subcategory);

  const [logo, setLogo] = useState("");
  const [bannerImg, setBannerImg] = useState("");
  const [iapImage, setIapImage] = useState("");
  const [logoCropState, setLogoCropState] = useState({
    isOpen: false,
    imageSrc: "",
    fileName: "logo-image.jpg",
  });
  const [bannerCropState, setBannerCropState] = useState({
    isOpen: false,
    imageSrc: "",
    fileName: "banner-image.jpg",
  });
  const [iapCropState, setIapCropState] = useState({
    isOpen: false,
    imageSrc: "",
    fileName: "iap-image.jpg",
  });
  const [itemCropState, setItemCropState] = useState({
    isOpen: false,
    imageSrc: "",
    fileName: "item-image.jpg",
    itemIndex: null,
  });
  const [submitMode, setSubmitMode] = useState("draft");
  const submitModeRef = useRef("draft");
  const logoFieldSetterRef = useRef(null);
  const bannerFieldSetterRef = useRef(null);
  const iapFieldSetterRef = useRef(null);
  const itemFieldSetterRef = useRef(null);
  const logoInputRef = useRef(null);
  const bannerInputRef = useRef(null);
  const iapInputRef = useRef(null);

  const user = useSelector((state) => state.user.userDetails);
  useEffect(() => {
    dispatch(getUserDetails());
  }, [dispatch]);

  /** ✅ pooled subcategories for selected categories */
  const [subPool, setSubPool] = useState([]);

  useEffect(() => {
    dispatch(getAllCategories());
  }, [dispatch]);

  useEffect(() => {
    if (isRedirect) {
      navigate("/items");
    }
  }, [isRedirect, navigate]);

  // Helper function to validate URLs
  function isValidUrl(url) {
    try {
      new URL(url);
      return true;
    } catch (_) {
      return false;
    }
  }

  const buildCroppedFileName = (originalName, prefix) => {
    const safePrefix = prefix || "cropped-image";
    const sourceName = String(originalName || "").trim();
    const baseName = sourceName.replace(/\.[^/.]+$/, "") || safePrefix;
    const normalizedBase = baseName
      .replace(/[^a-zA-Z0-9-_]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .toLowerCase();

    return `${normalizedBase || safePrefix}-${Date.now()}.jpg`;
  };

  /** ✅ fetch subs for multiple categories and merge (dedupe) */
  const fetchAndMergeSubcategories = async (categoryIds) => {
    try {
      const results = await Promise.all(
        categoryIds.map((cid) =>
          dispatch(fetchSubCategoryDataByCatagoryId({ category_id: cid })),
        ),
      );

      let merged = [];
      results.forEach((r) => {
        const payload = r?.payload;
        const list =
          payload?.result?.list ||
          payload?.result?.data ||
          payload?.result ||
          payload?.data ||
          payload ||
          [];

        if (Array.isArray(list)) merged = merged.concat(list);
      });

      const map = new Map();
      merged.forEach((s) => {
        const sid = Number(s?.id);
        if (!sid) return;
        if (!map.has(sid))
          map.set(sid, { id: sid, name: s?.name || `Sub ${sid}` });
      });

      setSubPool(Array.from(map.values()));
    } catch (e) {
      console.error(e);
    }
  };

  const onSubmitHandler = (values, mode) => {
    const isDraft = mode === "draft";
    // keep the status flags explicit for backend handling
    values.status = isDraft ? "draft" : "complete";
    values.isDraft = isDraft;

    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.debug("ItemGroupAdd submit", { status: values?.status, name: values?.name });
    }

    if (!isDraft) {
      if (!values.logo || !isValidUrl(values.logo)) {
        errorToastify("Challenge pack logo required");
        return false;
      }
      if (!values.bannerImg || !isValidUrl(values.bannerImg)) {
        errorToastify("Challenge pack banner image required");
        return false;
      }
      if (!values.iapimage || !isValidUrl(values.iapimage)) {
        errorToastify("IAP image required");
        return false;
      }
      if (!Array.isArray(values.items) || values.items.length === 0) {
        errorToastify("Challenges array is missing or empty.");
        return false;
      }
      for (let i = 0; i < values.items.length; i++) {
        const item = values.items[i];
        if (!item.item_img || !isValidUrl(item.item_img)) {
          errorToastify(`Challenge image is required on challenge ${i + 1}`);
          return false;
        }
      }
    }

    // ✅ validate multi-selects for new payload when completing
    const categoryIds = Array.isArray(values.categoryIds)
      ? values.categoryIds
      : [];
    const subCategoryIds = Array.isArray(values.subCategoryIds)
      ? values.subCategoryIds
      : [];

    if (!isDraft) {
      if (!categoryIds.length) {
        errorToastify("Please select at least one category");
        return false;
      }
      if (!subCategoryIds.length) {
        errorToastify("Please select at least one sub-category");
        return false;
      }
    }

    const payload = { ...values };
    delete payload.id;

    // ✅ ensure numbers in payload arrays
    payload.categoryIds = categoryIds.map(Number);
    payload.subCategoryIds = subCategoryIds.map(Number);

    // ✅ keep old fields for backward compatibility (first selected)
    payload.category = payload.categoryIds[0]
      ? String(payload.categoryIds[0])
      : "";
    payload.subCategory = payload.subCategoryIds[0]
      ? String(payload.subCategoryIds[0])
      : "";

    dispatch(createItemGroup(payload));
  };

  const closeLogoCropper = () => {
    if (logoCropState.imageSrc) {
      URL.revokeObjectURL(logoCropState.imageSrc);
    }
    setLogoCropState({
      isOpen: false,
      imageSrc: "",
      fileName: "logo-image.jpg",
    });
  };

  const fileUploadLogo = (event, setFieldValue) => {
    const file = event.currentTarget.files[0];
    if (!file) return;

    logoFieldSetterRef.current = setFieldValue;
    setLogoCropState({
      isOpen: true,
      imageSrc: URL.createObjectURL(file),
      fileName: file.name || "logo-image.jpg",
    });
    event.target.value = "";
  };

  const uploadLogoImageBlob = async (blob) => {
    try {
      const fileData = new FormData();
      const uploadFile = new File(
        [blob],
        buildCroppedFileName(logoCropState.fileName, "logo-image"),
        { type: "image/jpeg" },
      );
      fileData.append("file", uploadFile);
      const { data } = await instance.post(`/api/v1/common/file/upload`, fileData);
      if (data.success) {
        successToastify(data.message || "Uploaded");
        logoFieldSetterRef.current?.("logo", data.result.url);
        setLogo(data.result.url);
        closeLogoCropper();
      }
    } catch (err) {
      const msg = err?.response?.data?.message || "Logo upload failed";
      errorToastify(msg);
    }
  };

  const triggerFileInput = () => {
    logoInputRef.current?.click();
  };

  const handleCloseLogo = () => {
    setLogo("");
  };

  const closeBannerCropper = () => {
    if (bannerCropState.imageSrc) {
      URL.revokeObjectURL(bannerCropState.imageSrc);
    }
    setBannerCropState({
      isOpen: false,
      imageSrc: "",
      fileName: "banner-image.jpg",
    });
  };

  const fileUploadBanner = (event, setFieldValue) => {
    const file = event.currentTarget.files[0];
    if (!file) return;

    bannerFieldSetterRef.current = setFieldValue;
    setBannerCropState({
      isOpen: true,
      imageSrc: URL.createObjectURL(file),
      fileName: file.name || "banner-image.jpg",
    });
    event.target.value = "";
  };

  const uploadBannerImageBlob = async (blob) => {
    try {
      const fileData = new FormData();
      const uploadFile = new File(
        [blob],
        buildCroppedFileName(bannerCropState.fileName, "banner-image"),
        { type: "image/jpeg" },
      );
      fileData.append("file", uploadFile);
      const { data } = await instance.post(`/api/v1/common/file/upload`, fileData);
      if (data.success) {
        successToastify(data.message || "Uploaded");
        bannerFieldSetterRef.current?.("bannerImg", data.result.url);
        setBannerImg(data.result.url);
        closeBannerCropper();
      }
    } catch (err) {
      const msg = err?.response?.data?.message || "Banner upload failed";
      errorToastify(msg);
    }
  };

  const triggerFileInputBanner = () => {
    bannerInputRef.current?.click();
  };

  const handleCloseBanner = () => {
    setBannerImg("");
  };

  const closeItemCropper = () => {
    if (itemCropState.imageSrc) {
      URL.revokeObjectURL(itemCropState.imageSrc);
    }
    setItemCropState({
      isOpen: false,
      imageSrc: "",
      fileName: "item-image.jpg",
      itemIndex: null,
    });
  };

  const uploadItemImageBlob = async (blob) => {
    try {
      const fileData = new FormData();
      const uploadFile = new File(
        [blob],
        buildCroppedFileName(itemCropState.fileName, "item-image"),
        { type: "image/jpeg" },
      );
      fileData.append("file", uploadFile);

      const { data } = await instance.post(
        `/api/v1/common/file/upload`,
        fileData,
      );
      if (data.success) {
        successToastify(data.message || "Uploaded");
        const itemIndex = itemCropState.itemIndex;
        if (itemIndex !== null) {
          itemFieldSetterRef.current?.(
            `items.${itemIndex}.item_img`,
            data.result.url,
          );
        }
        closeItemCropper();
      }
    } catch (err) {
      const msg = err?.response?.data?.message || "Challenge upload failed";
      errorToastify(msg);
    }
  };

  const fileUploadItem = (event, index, setFieldValue) => {
    const file = event.currentTarget.files[0];
    if (!file) return;

    itemFieldSetterRef.current = setFieldValue;
    setItemCropState({
      isOpen: true,
      imageSrc: URL.createObjectURL(file),
      fileName: file.name || "item-image.jpg",
      itemIndex: index,
    });
    event.target.value = "";
  };

  const closeIapCropper = () => {
    if (iapCropState.imageSrc) {
      URL.revokeObjectURL(iapCropState.imageSrc);
    }
    setIapCropState({
      isOpen: false,
      imageSrc: "",
      fileName: "iap-image.jpg",
    });
  };

  const uploadIapImageBlob = async (blob) => {
    try {
      const fileData = new FormData();
      const uploadFile = new File(
        [blob],
        buildCroppedFileName(iapCropState.fileName, "iap-image"),
        { type: "image/jpeg" },
      );
      fileData.append("file", uploadFile);

      const { data } = await instance.post(`/api/v1/common/file/upload`, fileData);

      const uploadedUrl = resolveUploadUrl(data);
      if (data.success && uploadedUrl) {
        successToastify(data.message || "Uploaded");
        iapFieldSetterRef.current?.("iapimage", uploadedUrl);
        setIapImage(uploadedUrl);
        closeIapCropper();
      } else if (data.success) {
        errorToastify("Upload succeeded but no image URL was returned.");
      }
    } catch (err) {
      const msg = err?.response?.data?.message || "IAP Image upload failed";
      errorToastify(msg);
    }
  };

  const fileUploadIapImage = async (event, setFieldValue) => {
    const file = event.currentTarget.files[0];
    if (!file) return;

    iapFieldSetterRef.current = setFieldValue;
    setIapCropState({
      isOpen: true,
      imageSrc: URL.createObjectURL(file),
      fileName: file.name || "iap-image.jpg",
    });
    event.target.value = "";
  };
  const triggerFileInputIap = () => {
    iapInputRef.current?.click();
  };

  const handleCloseIap = () => {
    setIapImage("");
  };

  /** category options */
  const categoryOptions = useMemo(() => {
    return Array.isArray(allCategory)
      ? allCategory.map((c) => ({ id: c.id, name: c.name }))
      : [];
  }, [allCategory]);

  /** sub options: pool first else store */
  const subCategoryOptions = useMemo(() => {
    if (Array.isArray(subPool) && subPool.length) return subPool;

    if (Array.isArray(allSubCatagories) && allSubCatagories.length) {
      const map = new Map();
      allSubCatagories.forEach((s) => {
        const sid = Number(s?.id);
        if (!sid) return;
        if (!map.has(sid))
          map.set(sid, { id: sid, name: s?.name || `Sub ${sid}` });
      });
      return Array.from(map.values());
    }
    return [];
  }, [subPool, allSubCatagories]);

  /** ensure selected are always present in dropdown options */
  const buildSubOptionsWithSelected = (options, selectedIds) => {
    const map = new Map((options || []).map((x) => [Number(x.id), x]));
    (selectedIds || []).forEach((sid) => {
      const id = Number(sid);
      if (!id) return;
      if (!map.has(id)) map.set(id, { id, name: `Selected (${id})` });
    });
    return Array.from(map.values());
  };

  return (
    <div id="main" className="main">
      <div className="pagetitle">
        <h1>Add Challenge Pack</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/items">Challenge Packs</Link>
            </li>
            <li className="breadcrumb-item active">Add</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="container">
          <div className="row">
            <div className="col-md-8">
              <Formik
                initialValues={itemGroupData}
                validationSchema={
                  submitMode === "complete"
                    ? itemGroupSchema
                    : itemGroupDraftSchema
                }
                validateOnChange={submitMode === "complete"}
                validateOnBlur={submitMode === "complete"}
                onSubmit={(vals) =>
                  onSubmitHandler(vals, submitModeRef.current)
                }
              >
                {({
                  values,
                  setFieldValue,
                  errors,
                  touched,
                  submitForm,
                  setErrors,
                  setTouched,
                }) => {
                  /** ✅ Category toggle (multi) */
                  const toggleCategory = async (catId) => {
                    const current = Array.isArray(values.categoryIds)
                      ? values.categoryIds
                      : [];
                    const idNum = Number(catId);
                    const exists = current.includes(idNum);

                    const next = exists
                      ? current.filter((x) => x !== idNum)
                      : [...current, idNum];
                    setFieldValue("categoryIds", next);

                    // ✅ keep old single category synced (first selected)
                    setFieldValue("category", next[0] ? String(next[0]) : "");

                    // ✅ rebuild sub pool for ALL selected categories so old selected subs still show
                    setSubPool([]);
                    if (next.length) {
                      await fetchAndMergeSubcategories(next);
                    }
                  };

                  /** ✅ Subcategory toggle (multi) */
                  const toggleSubCategory = (subId) => {
                    const current = Array.isArray(values.subCategoryIds)
                      ? values.subCategoryIds
                      : [];
                    const idNum = Number(subId);
                    const exists = current.includes(idNum);

                    const next = exists
                      ? current.filter((x) => x !== idNum)
                      : [...current, idNum];
                    setFieldValue("subCategoryIds", next);

                    // ✅ keep old single subCategory synced (first selected)
                    setFieldValue(
                      "subCategory",
                      next[0] ? String(next[0]) : "",
                    );
                  };

                  const finalSubCategoryOptions = buildSubOptionsWithSelected(
                    subCategoryOptions,
                    values.subCategoryIds,
                  );

                  return (
                    <Form>
                      <div className="row mb-3">
                        <div className="col-md-12">
                          <LevelReq htmlFor="name" className="form-label fs-7">
                            Challenge Pack Name
                          </LevelReq>
                          <Field
                            type="text"
                            className="form-control"
                            key="name"
                            id="name"
                            name="name"
                          />
                          <InputError name="name" />
                        </div>
                      </div>

                      <div className="row mb-3">
                        <div className="col-md-12">
                          <LevelReq
                            htmlFor="description"
                            className="form-label fs-7"
                          >
                            Challenge Pack Description
                          </LevelReq>
                          <Field
                            as="textarea"
                            id="description"
                            name="description"
                            className="form-control"
                            rows="4"
                          />
                          <InputError name="description" />
                        </div>
                      </div>

                      {/* ✅ NEW Multi-select Category + SubCategory (payload wants arrays) */}
                      <div className="row mb-1">
                        <MultiSelectDropdown
                          label="Category"
                          placeholder="Search category..."
                          options={categoryOptions}
                          selectedIds={
                            Array.isArray(values.categoryIds)
                              ? values.categoryIds
                              : []
                          }
                          onToggle={toggleCategory}
                          onDone={() => {}}
                          required
                          invalid={Boolean(
                            touched.categoryIds && errors.categoryIds,
                          )}
                          errorName="categoryIds"
                        />

                        <MultiSelectDropdown
                          label="Sub-Category"
                          placeholder="Search sub-category..."
                          options={finalSubCategoryOptions}
                          selectedIds={
                            Array.isArray(values.subCategoryIds)
                              ? values.subCategoryIds
                              : []
                          }
                          onToggle={toggleSubCategory}
                          onDone={() => {}}
                          required
                          disabled={
                            !Array.isArray(values.categoryIds) ||
                            values.categoryIds.length === 0
                          }
                          invalid={Boolean(
                            touched.subCategoryIds && errors.subCategoryIds,
                          )}
                          errorName="subCategoryIds"
                        />
                      </div>

                      {/* ✅ keep old schema fields (hidden) */}
                      <Field type="hidden" id="category" name="category" />
                      <Field
                        type="hidden"
                        id="subCategory"
                        name="subCategory"
                      />

                      <div className="row mb-3">
                        <div className="col-md-12">
                          <label htmlFor="price" className="form-label fs-7">
                            Price
                          </label>
                          <Field
                            type="text"
                            className="form-control"
                            key="price"
                            id="price"
                            name="price"
                            onChange={(e) => {
                              const value = e.target.value;
                              if (
                                value === "" ||
                                value === "." ||
                                /^\d*\.?\d*$/.test(value)
                              ) {
                                setFieldValue("price", value);
                              }
                            }}
                          />
                          <InputError name="price" />
                        </div>
                      </div>

                      <div className="row mb-3">
                        <div className="col-md-6">
                          <label
                            htmlFor="package_logo"
                            className="form-label fs-7"
                          >
                            Logo (Aspect Ratio :- <b>1:1</b>)
                          </label>
                          <br />
                          {logo ? (
                            <div
                              style={{
                                position: "relative",
                                width: "100px",
                                height: "100px",
                              }}
                            >
                              <img
                                src={logo}
                                alt="logo_group"
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  handleCloseLogo();
                                  setFieldValue("logo", "");
                                }}
                                style={{
                                  position: "absolute",
                                  top: "-10px",
                                  right: "-10px",
                                  backgroundColor: "red",
                                  color: "white",
                                  border: "none",
                                  borderRadius: "50%",
                                  cursor: "pointer",
                                  width: "20px",
                                  height: "20px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                &times;
                              </button>
                            </div>
                          ) : (
                            <>
                              <input
                                ref={logoInputRef}
                                id="package_logo"
                                name="package_logo"
                                type="file"
                                accept="image/*"
                                onChange={(event) =>
                                  fileUploadLogo(event, setFieldValue)
                                }
                                style={{ display: "none" }}
                              />
                              <Button
                                type="button"
                                className="btn btn-sm btn-outline-primary me-2"
                                title="Add Logo"
                                onClick={triggerFileInput}
                              >
                                <i className="fa-solid fa-upload"></i>
                              </Button>
                            </>
                          )}
                          <InputError name="logo" />
                          <IapImageCropperModal
                            isOpen={logoCropState.isOpen}
                            imageSrc={logoCropState.imageSrc}
                            onCancel={closeLogoCropper}
                            onConfirm={uploadLogoImageBlob}
                            title="Crop Logo Image"
                            targetWidth={1}
                            targetHeight={1}
                            useSourceDimensions
                          />
                        </div>

                        <div className="col-md-6">
                          <label
                            htmlFor="package_logo"
                            className="form-label fs-7"
                          >
                            Banner Image (Aspect Ratio :- <b>16:9</b>)
                          </label>
                          <br />
                          {bannerImg ? (
                            <div
                              style={{
                                position: "relative",
                                width: "100px",
                                height: "100px",
                              }}
                            >
                              <img
                                src={bannerImg}
                                alt="Banner_group"
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  handleCloseBanner();
                                  setFieldValue("bannerImg", "");
                                }}
                                style={{
                                  position: "absolute",
                                  top: "-10px",
                                  right: "-10px",
                                  backgroundColor: "red",
                                  color: "white",
                                  border: "none",
                                  borderRadius: "50%",
                                  cursor: "pointer",
                                  width: "20px",
                                  height: "20px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                &times;
                              </button>
                            </div>
                          ) : (
                            <>
                              <input
                                ref={bannerInputRef}
                                id="banner_img"
                                name="banner_img"
                                type="file"
                                accept="image/*"
                                onChange={(event) =>
                                  fileUploadBanner(event, setFieldValue)
                                }
                                style={{ display: "none" }}
                              />
                              <Button
                                type="button"
                                className="btn btn-sm btn-outline-primary me-2"
                                title="Add Banner Image"
                                onClick={triggerFileInputBanner}
                              >
                                <i className="fa-solid fa-upload"></i>
                              </Button>
                            </>
                          )}
                          <InputError name="bannerImg" />
                          <IapImageCropperModal
                            isOpen={bannerCropState.isOpen}
                            imageSrc={bannerCropState.imageSrc}
                            onCancel={closeBannerCropper}
                            onConfirm={uploadBannerImageBlob}
                            title="Crop Banner Image"
                            targetWidth={16}
                            targetHeight={9}
                            useSourceDimensions
                          />
                        </div>

                        <div className="col-md-6">
                          <label
                            htmlFor="iap_img_add"
                            className="form-label fs-7"
                          >
                            IAP Image (Aspect Ratio :- <b>1260 × 2736</b>)
                          </label>
                          <div
                            style={{
                              color: "red",
                              fontSize: "12px",
                              marginTop: "-5px",
                              marginBottom: "6px",
                            }}
                          >
                            This image is mandatory for App Store listing.
                          </div>
                          <br />

                          {iapImage ? (
                            <div
                              style={{
                                position: "relative",
                                width: "100px",
                                height: "100px",
                              }}
                            >
                              <img
                                src={iapImage}
                                alt="IAP"
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  handleCloseIap();
                                  setFieldValue("iapimage", "");
                                }}
                                style={{
                                  position: "absolute",
                                  top: "-10px",
                                  right: "-10px",
                                  backgroundColor: "red",
                                  color: "white",
                                  border: "none",
                                  borderRadius: "50%",
                                  cursor: "pointer",
                                  width: "20px",
                                  height: "20px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                &times;
                              </button>
                            </div>
                          ) : (
                            <>
                              <input
                                ref={iapInputRef}
                                id="iap_img_add"
                                name="iap_img"
                                type="file"
                                accept="image/*"
                                onChange={(event) =>
                                  fileUploadIapImage(event, setFieldValue)
                                }
                                style={{ display: "none" }}
                              />
                              <Button
                                type="button"
                                className="btn btn-sm btn-outline-primary me-2"
                                title="Add IAP Image"
                                onClick={triggerFileInputIap}
                              >
                                <i className="fa-solid fa-upload"></i>
                              </Button>
                            </>
                          )}

                          <InputError name="iapimage" />
                          <IapImageCropperModal
                            isOpen={iapCropState.isOpen}
                            imageSrc={iapCropState.imageSrc}
                            onCancel={closeIapCropper}
                            onConfirm={uploadIapImageBlob}
                            title="Crop IAP Image"
                          />
                        </div>
                      </div>

                      <Field type="hidden" id="id" name="id" />
                      <hr />

                      <FieldArray name="items">
                        {({ remove, push }) => (
                          <div>
                            {values.items.map((item, index) => (
                              <div key={index}>
                                <div className="row">
                                  <div
                                    className="col-md-12"
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      width: "100%",
                                    }}
                                  >
                                    <h4 style={{ margin: 0 }}>
                                      Challenge {index + 1}
                                    </h4>
                                    {values.items.length > 1 ? (
                                      <button
                                        type="button"
                                        onClick={() => remove(index)}
                                        className="btn btn-sm btn-outline-danger me-2"
                                        style={{ marginLeft: "auto" }}
                                      >
                                        <i className="fa-solid fa-xmark"></i>
                                      </button>
                                    ) : null}
                                  </div>
                                </div>

                                <div className="row mb-3">
                                  <div className="col-md-12">
                                    <LevelReq className="form-label fs-7">
                                      Challenge Name
                                    </LevelReq>
                                    <Field
                                      name={`items.${index}.item_name`}
                                      type="text"
                                      className="form-control"
                                    />
                                    {touched.items?.[index]?.item_name ? (
                                      <InlineFieldError
                                        message={errors.items?.[index]?.item_name}
                                      />
                                    ) : null}
                                  </div>
                                </div>

                                <div className="row mb-3">
                                  <div className="col-md-12">
                                    <label className="form-label fs-7">
                                      Challenge Description (optional)
                                    </label>
                                    <Field
                                      className="form-control"
                                      name={`items.${index}.item_desc`}
                                      type="text"
                                      as="textarea"
                                      rows={3}
                                    />
                                    {touched.items?.[index]?.item_desc ? (
                                      <InlineFieldError
                                        message={errors.items?.[index]?.item_desc}
                                      />
                                    ) : null}
                                  </div>
                                </div>

                                <div className="row mb-3">
                                  <div className="col-md-12">
                                    <LevelReq className="form-label fs-7">
                                      Challenge Image (Aspect Ratio :- <b>1:1</b>)
                                    </LevelReq>
                                    <br />
                                    <input
                                      id={`item_img_${index}`}
                                      name={`items.${index}.item_img`}
                                      type="file"
                                      accept="image/*"
                                      style={{ display: "none" }}
                                      onChange={(event) =>
                                        fileUploadItem(
                                          event,
                                          index,
                                          setFieldValue,
                                        )
                                      }
                                    />
                                    {item.item_img ? (
                                      <div
                                        style={{
                                          position: "relative",
                                          width: "100px",
                                          height: "100px",
                                        }}
                                      >
                                        <img
                                          src={item.item_img}
                                          alt="Challenge"
                                          style={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "cover",
                                          }}
                                        />
                                        <button
                                          type="button"
                                          onClick={() =>
                                            setFieldValue(
                                              `items.${index}.item_img`,
                                              "",
                                            )
                                          }
                                          style={{
                                            position: "absolute",
                                            top: "-10px",
                                            right: "-10px",
                                            backgroundColor: "red",
                                            color: "white",
                                            border: "none",
                                            borderRadius: "50%",
                                            cursor: "pointer",
                                            width: "20px",
                                            height: "20px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                          }}
                                        >
                                          &times;
                                        </button>
                                      </div>
                                    ) : (
                                      <button
                                        type="button"
                                        onClick={() =>
                                          document
                                            .getElementById(`item_img_${index}`)
                                            ?.click()
                                        }
                                        className="btn btn-sm btn-outline-primary me-2"
                                      >
                                        <i className="fa-solid fa-upload"></i>
                                      </button>
                                    )}
                                    {touched.items?.[index]?.item_img ? (
                                      <InlineFieldError
                                        message={errors.items?.[index]?.item_img}
                                      />
                                    ) : null}
                                  </div>
                                </div>
                              </div>
                            ))}

                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary me-2 mb-3"
                              onClick={() =>
                                push({
                                  item_name: "",
                                  item_desc: "",
                                  item_img: "",
                                  position: values.items.length,
                                })
                              }
                            >
                              Add Challenge
                            </button>
                          </div>
                        )}
                      </FieldArray>

                      <IapImageCropperModal
                        isOpen={itemCropState.isOpen}
                        imageSrc={itemCropState.imageSrc}
                        onCancel={closeItemCropper}
                        onConfirm={uploadItemImageBlob}
                        title="Crop Challenge Image"
                        targetWidth={1000}
                        targetHeight={1000}
                        useSourceDimensions
                      />

                      <div className="col-12 mb-3 d-flex gap-2">
                        <button
                          type="button"
                          className="btn btn-outline-secondary w-50"
                          disabled={isBtnLoad}
                          onClick={() => {
                            flushSync(() => {
                              submitModeRef.current = "draft";
                              setSubmitMode("draft");
                              setErrors({});
                              setTouched({});
                            });
                            submitForm();
                          }}
                        >
                          {isBtnLoad && submitMode === "draft" ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm"
                                role="status"
                                aria-hidden="true"
                              ></span>
                              Saving...
                            </>
                          ) : (
                            <>Save as Draft</>
                          )}
                        </button>
                        {user?.roleid === '1' && (

                          <button
                          type="button"
                          className="btn btn-primary w-50"
                          disabled={isBtnLoad}
                          onClick={async () => {
                            try {
                              await itemGroupSchema.validate(values, {
                                abortEarly: false,
                              });
                            } catch (err) {
                              const formErrors = {};
                              if (Array.isArray(err.inner) && err.inner.length) {
                                err.inner.forEach((e) => {
                                  if (e.path) {
                                    setIn(formErrors, e.path, e.message);
                                  }
                                });
                              } else if (err.message) {
                                formErrors.form = err.message;
                              }
                              setErrors(formErrors);
                              setTouched(
                                buildItemGroupCompleteTouched(values),
                                false,
                              );
                              errorToastify(
                                firstFormikErrorMessage(formErrors) ||
                                  "Please complete all required fields.",
                              );
                              return;
                            }
                            flushSync(() => {
                              submitModeRef.current = "complete";
                              setSubmitMode("complete");
                            });
                            setTouched(
                              buildItemGroupCompleteTouched(values),
                              false,
                            );
                            submitForm();
                          }}
                        >
                          {isBtnLoad && submitMode === "complete" ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm"
                                role="status"
                                aria-hidden="true"
                                ></span>
                              Submitting...
                            </>
                          ) : (
                            <>Complete</>
                          )}
                        </button>
                          )}
                      </div>
                    </Form>
                  );
                }}
              </Formik>
            </div>

            <div className="col-md-4"></div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ItemGroupAddPage;
